/**
 * POS Sales KPIs
 *
 * Display key performance indicators for POS sales.
 */

import { Receipt, XCircle, Euro } from 'lucide-react';
import { useCurrency } from '@/hooks';
import { formatCentimes } from '@/lib/format';
import type { PosReceiptsKPIs } from '@bstock/shared';

interface PosSalesKPIsProps {
  kpis: PosReceiptsKPIs | undefined;
  isLoading?: boolean;
  showFinancials?: boolean;
}

interface KPICardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  isLoading?: boolean;
}

function KPICard({ label, value, icon, isLoading }: KPICardProps) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{label}</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">
            {isLoading ? (
              <span className="inline-block w-16 h-8 bg-gray-200 rounded animate-pulse" />
            ) : (
              value
            )}
          </p>
        </div>
        <div className="p-3 bg-gray-100 rounded-full text-gray-600">
          {icon}
        </div>
      </div>
    </div>
  );
}

export function PosSalesKPIs({ kpis, isLoading, showFinancials = false }: PosSalesKPIsProps) {
  const currency = useCurrency();

  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 ${showFinancials ? 'lg:grid-cols-4' : 'lg:grid-cols-2'} gap-4`}>
      <KPICard
        label="Tickets importes"
        value={kpis?.totalReceipts ?? 0}
        icon={<Receipt className="w-6 h-6" />}
        isLoading={isLoading}
      />
      <KPICard
        label="Tickets annules"
        value={kpis?.cancelledCount ?? 0}
        icon={<XCircle className="w-6 h-6" />}
        isLoading={isLoading}
      />
      {showFinancials && (
        <>
          <KPICard
            label="CA TTC (tickets)"
            value={formatCentimes(kpis?.totalAmountTTC ?? 0, currency)}
            icon={<Euro className="w-6 h-6" />}
            isLoading={isLoading}
          />
          <KPICard
            label="CA HT (lignes)"
            value={formatCentimes(kpis?.totalAmountHT ?? 0, currency)}
            icon={<Euro className="w-6 h-6" />}
            isLoading={isLoading}
          />
        </>
      )}
    </div>
  );
}
